// Ported from vlookup-dramatic/app/for-organisations/page.tsx by scripts/revamp/port-design.js
import React, { lazy, Suspense, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import Image from "../../components/image";
import Link from "../../components/link";
import { ArrowUpRight } from "../../lib/icons";
import { ButtonLink } from "../../components/button-link";
import { Icon } from "../../components/icon";
import { brandAccents } from "../../lib/accents";
import { forOrgHero, forOrgHeroV3, forOrgOffers, forOrgProcess, forOrgTracks, forOrgProgrammes, forOrgProgrammeLinks, forOrgWhy, partners, forOrgSectionHeads, forOrgWorkshopStrip, forOrgCrossSell, forOrgFinalCta, } from "../../lib/content";
import * as settingActions from "../../../actions/settingActions";
import { agent } from "../../../utils/agent";
import * as API from "../../../utils/apiPath";
import { usePageMeta } from "../../hooks/use-page-meta";
import { useHashScrollOnMount } from "../../lib/section-nav";

const OrgTestimonialCarousel = lazy(() => import("./testimonial-carousel"));
const OrgGalleryCarousel = lazy(() => import("./gallery-carousel"));

const PAGE_META = {
  title: "vLookUp Programmes — For Organisations | vLookUp",
  description: forOrgHeroV3.subtitle,
};

/* ── Live data (same calls as the old /programs page, src/Pages/Programs) ─────
   - GET pages/programs (CMS): ONE rich-text block. The design is a fixed,
     structured layout (plan decision: keep the layout, use the CMS text only
     where there is a paragraph slot). The only slot is the hero intro: the
     first real paragraph of the CMS body replaces the design's subtitle when
     USE_CMS_INTRO is true. meta_title/meta_description are used when the CMS
     has something better than the bare word "Programs".
   - GET brand: partner names in the marquee, written out as text (QA 31 Aug;
     design's text list is the fallback when the API returns nothing).
   - GET setting (loaded by the shell): the three hero numbers. */
const USE_CMS_INTRO = true;

function firstParagraph(html) {
  if (!html) return "";
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const texts = Array.from(doc.querySelectorAll("p")).map((el) => (el.textContent || "").replace(/\s+/g, " ").trim());
    return texts.find((t) => t.length > 80) || "";
  } catch (e) {
    return "";
  }
}

const fmt = (n) => Number(n).toLocaleString("en-US");
const statsFromSetting = (st) => st && st.organisations != null
  ? [
    { n: fmt(st.organisations) + "+", l: "Organisations" },
    { n: fmt(st.countries), l: "Countries" },
    { n: fmt(st.mentoring_hours) + "+", l: "Mentoring hours" },
  ]
  : forOrgHero.stats;

const meaningful = (v) => typeof v === "string" && v.trim().length > 12;

/** Narrow British-English pass for CMS-sourced copy (QA row: programme / personalised). */
function britishise(s) {
  return String(s || "")
    .replace(/personalized/g, "personalised")
    .replace(/Personalized/g, "Personalised")
    .replace(/\bprograms\b/g, "programmes")
    .replace(/\bPrograms\b/g, "Programmes")
    .replace(/\bprogram\b/g, "programme")
    .replace(/\bProgram\b/g, "Programme");
}

export default function ForOrganisationsPage() {
  const dispatch = useDispatch();
  const brand = useSelector((s) => s.brand) || [];
  const setting = useSelector((s) => s.setting) || {};
  const [cms, setCms] = useState({});

  useEffect(() => {
    dispatch(settingActions.getBrandData());
    let alive = true;
    agent.get(API.PROGRAMS)
      .then((res) => { if (alive) setCms((res.data && res.data.data) || {}); })
      .catch(() => { });
    return () => { alive = false; };
  }, [dispatch]);

  usePageMeta({
    title: meaningful(cms.meta_title) ? cms.meta_title : PAGE_META.title,
    description: meaningful(cms.meta_description) ? cms.meta_description : PAGE_META.description,
  });

  const cmsIntro = USE_CMS_INTRO ? firstParagraph(cms.description) : "";
  // QA (31 Aug): British spellings on this page — the CMS body is edited in the
  // admin, so normalise the few US spellings here as well (personalised, programme).
  const intro = britishise(cmsIntro || forOrgHeroV3.subtitle);
  // Dynamic stats (GET get-stats?category=Programme)
  const [dynamicStats, setDynamicStats] = useState(null);
  useEffect(() => {
    let alive = true;
    agent
      .get(`${API.GET_STATS}?category=Programme`)
      .then((res) => {
        if (!alive) return;
        const raw = res?.data?.data ?? res?.data;
        const rows = Array.isArray(raw) ? raw : [];
        const programmeRows = rows
          .filter((r) => {
            const isActive = r && r.status !== false && r.status !== 0 && r.status !== "false" && r.status !== "0";
            const isProg = !r?.category || String(r.category).trim().toLowerCase() === "programme";
            return isActive && isProg && r.heading && r.count != null;
          })
          .sort((a, b) => {
            if (a.display_order != null && b.display_order != null) {
              return Number(a.display_order) - Number(b.display_order);
            }
            return 0;
          })
          .map((r) => ({
            id: r.id,
            n: r.count,
            l: r.heading,
          }));

        if (programmeRows.length > 0) {
          setDynamicStats(programmeRows);
        }
      })
      .catch(() => { });

    return () => {
      alive = false;
    };
  }, []);

  const stats = (dynamicStats && dynamicStats.length > 0)
    ? dynamicStats
    : statsFromSetting(setting);
  // QA (31 Aug): the partner strip shows written-out organisation names (same
  // treatment as the design's home marquee), not logo images.
  const orgNames = brand.map((b) => (b.title || "").trim()).filter(Boolean);
  useHashScrollOnMount();

  return (<div className="overflow-x-clip">
    <style>{`
        @keyframes orgMarquee { to { transform: translateX(-50%); } }
      `}</style>

    {/* ===================== HERO + TESTIMONIAL CAROUSEL ===================== */}
    <section className="relative overflow-hidden">
      <div aria-hidden className="pointer-events-none absolute -left-48 -top-24 -z-10 size-[520px] rounded-full bg-brand-blue/15 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute -right-40 top-40 -z-10 size-[460px] rounded-full bg-brand-blue/15 blur-3xl" />
      <div className="wrap grid items-start gap-14 py-10 pb-14 md:grid-cols-[1.05fr_0.95fr] md:py-14 md:pb-16">
        {/* Left — copy */}
        <div className="md:pt-4">
          <span className="mb-5 flex items-center gap-2.5 text-xs font-bold uppercase tracking-[0.2em] text-brand-red">
            <span className="h-px w-8 bg-current opacity-50" />
            {forOrgHeroV3.eyebrow}
          </span>
          <h1 className="font-display text-5xl font-semibold leading-[0.95] tracking-tight text-heading sm:text-6xl xl:text-7xl">
            Mentoring programmes designed for{" "}
            <em className="font-normal italic text-brand-red">
              organisations &amp; institutions
            </em>
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg">
            {intro}
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <ButtonLink href={forOrgHeroV3.primaryCta.href} className="group h-auto rounded-full bg-brand-red py-4 pl-7 pr-3 text-base font-bold text-white hover:bg-brand-red-dark">
              {forOrgHeroV3.primaryCta.label}
              <span className="cta-arrow-chip ml-3 grid size-9 place-items-center rounded-full bg-white/20 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5">
                <ArrowUpRight className="size-4" />
              </span>
            </ButtonLink>
            <ButtonLink href={forOrgHeroV3.secondaryCta.href} variant="ghost" className="h-auto rounded-full border border-brand-navy/20 px-7 py-4 text-base font-bold text-brand-navy hover:bg-brand-navy hover:text-white">
              {forOrgHeroV3.secondaryCta.label}
            </ButtonLink>
          </div>

          {/* QA (31 Aug): fixed 3-up grid so the stats never roll into a gappy
                second line at tablet widths. */}
          <dl className="mt-10 grid grid-cols-3 gap-x-6 gap-y-6 border-t border-brand-navy/10 pt-7">
            {stats.map((s, idx) => (<div key={s.id != null ? `${s.id}-${idx}` : `${s.l}-${idx}`}>
              <dt className="font-display text-2xl font-semibold text-brand-navy md:text-3xl xl:text-4xl">
                {s.n}
              </dt>
              <dd className="mt-1 text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground">
                {s.l}
              </dd>
            </div>))}
          </dl>
        </div>

        {/* Right — rotating testimonial carousel */}
        <Suspense fallback={null}>
          <OrgTestimonialCarousel />
        </Suspense>
      </div>
    </section>

    <nav aria-label="For organisations sections" className="sticky top-[76px] z-40 border-y border-neutral-200 bg-white/85 backdrop-blur-md">
      <div className="wrap flex items-center gap-1 overflow-x-auto py-2.5 text-sm">
        {[
          { label: "What we offer", href: "#offer" },
          { label: "How it works", href: "#process" },
          { label: "Who we serve", href: "#tracks" },
          { label: "Showcase", href: "#programmes" },
          { label: "Why us", href: "#why" },
        ].map((l) => (<Link key={l.href} href={l.href} className="shrink-0 rounded-full px-4 py-1.5 font-bold text-neutral-500 transition-colors hover:bg-brand-navy/5 hover:text-brand-navy">
          {l.label}
        </Link>))}
        <Link href="#contact" className="ml-auto hidden shrink-0 rounded-full bg-brand-red px-4 py-1.5 font-bold text-white transition-colors hover:bg-brand-red-dark md:inline-block">
          Reach out
        </Link>
      </div>
    </nav>

    {/* ===================== PARTNERS MARQUEE (moved below banner) ===================== */}
    <section aria-label="Trusted by leading organisations" className="border-y border-brand-navy/10 py-6">
      <p className="wrap text-center text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground">
        Our partners — trusted by leading organisations
      </p>
      <div className="relative mt-5 overflow-hidden">
        <div aria-hidden className="pointer-events-none absolute inset-y-0 left-0 z-10 w-16 bg-gradient-to-r from-cream to-transparent md:w-28" />
        <div aria-hidden className="pointer-events-none absolute inset-y-0 right-0 z-10 w-16 bg-gradient-to-l from-cream to-transparent md:w-28" />
        {/* QA (31 Aug): written-out organisation names (dev-site treatment) —
              standardised and neat regardless of logo artwork. Names come from
              GET brand titles, with the design's list as fallback. */}
        <div className="flex w-max animate-[orgMarquee_36s_linear_infinite] hover:[animation-play-state:paused] motion-reduce:animate-none">
          {[0, 1].map((copy) => (<ul key={copy} aria-hidden={copy === 1} className="flex shrink-0 items-center gap-14 pr-14">
            {(orgNames.length ? orgNames : partners).map((p) => (<li key={`${copy}-${p}`} className="whitespace-nowrap font-display text-xl font-semibold text-brand-navy/40 transition-colors hover:text-brand-navy">
              {p}
            </li>))}
          </ul>))}
        </div>
      </div>
    </section>

    {/* ===================== EDITORIAL FEATURE BAND ===================== */}
    <section className="pb-4 pt-10 md:pb-8 md:pt-14">
      <div className="wrap">
        <figure className="relative overflow-hidden rounded-[2.5rem] shadow-[0_40px_90px_-55px_rgba(26,58,92,0.6)]">
          <div className="relative aspect-[16/11] sm:aspect-[16/8] md:aspect-[21/8]">
            <Image src="/revamp/images/fo-real-connections.webp" alt="Women connecting at a vLookUp mentoring programme event" fill width={1680} height={640} sizes="(max-width: 768px) 92vw, 1120px" priority className="object-cover" />
            <div aria-hidden className="absolute inset-0 scrim-overlay" />
          </div>
          <figcaption className="absolute inset-y-0 left-0 flex max-w-md flex-col justify-end p-8 md:p-12">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-cream/85">
              In the room
            </p>
            <p className="mt-3 font-display text-2xl font-semibold leading-tight text-white md:text-4xl">
              Real connections, made in{" "}
              <em className="font-normal italic text-cream">real rooms.</em>
            </p>
          </figcaption>
        </figure>
      </div>
    </section>

    {/* ===================== WHAT WE OFFER ===================== */}
    <section id="offer" className="relative scroll-mt-32 overflow-hidden py-14 md:py-20">
      <div className="wrap">
        <SectionHead eyebrow={forOrgSectionHeads.offer.eyebrow} title={accent(forOrgSectionHeads.offer.heading, "we handle everything")} sub={forOrgSectionHeads.offer.sub} />
        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {forOrgOffers.map((o) => (<article key={o.title} className="group relative overflow-hidden rounded-[1.75rem] border border-brand-navy/10 bg-card p-7 transition-all hover:-translate-y-1 hover:shadow-xl">
            <div className="flex items-start">
              <Icon name={o.icon} className="size-13" />
            </div>
            <h3 className="mt-6 font-display text-2xl font-semibold text-heading">
              {o.title}
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              {o.desc}
            </p>
          </article>))}
        </div>
      </div>
    </section>

    {/* ===================== HOW IT WORKS (id="process") ===================== */}
    <section id="process" className="scroll-mt-32 bg-cream-deep py-14 md:py-20">
      <div className="wrap">
        <SectionHead eyebrow={forOrgSectionHeads.process.eyebrow} title={accent(forOrgSectionHeads.process.heading, "impact")} />
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {forOrgProcess.map((s) => (<div key={s.n} className="relative rounded-[1.75rem] bg-card p-7 shadow-sm">
            <h3 className="font-display text-2xl font-semibold text-heading">
              {s.title}
            </h3>
            <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">
              {s.desc}
            </p>
          </div>))}
        </div>
      </div>
    </section>

    <section id="workshops" className="scroll-mt-32 py-8 md:py-12">
      <div className="wrap">
        <div className="relative overflow-hidden rounded-[2.5rem] border border-brand-navy/10 bg-card px-8 py-12 md:px-16 md:py-14">
          <div aria-hidden className="pointer-events-none absolute -right-24 -top-24 size-[360px] rounded-full bg-brand-blue/15 blur-3xl" />
          <div className="relative flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
            <div className="max-w-2xl">
              <span className="flex items-center gap-2.5 text-xs font-bold uppercase tracking-[0.2em] text-brand-red">
                <span className="h-px w-8 bg-current opacity-50" />
                {forOrgWorkshopStrip.eyebrow}
              </span>
              <h2 className="mt-3 font-display text-3xl font-semibold text-heading md:text-4xl">
                {forOrgWorkshopStrip.heading}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                {forOrgWorkshopStrip.sub}
              </p>
              {forOrgWorkshopStrip.clients && forOrgWorkshopStrip.clients.length > 0 && (
                <div className="mt-6 border-t border-brand-navy/10 pt-5">
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-brand-navy/50">
                    {forOrgWorkshopStrip.label || "Clients"}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2.5">
                    {forOrgWorkshopStrip.clients.map((c) => (
                      <span key={c} className="rounded-full bg-cream-deep px-4 py-2 text-sm font-bold text-brand-navy">
                        {c}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <ButtonLink href={forOrgWorkshopStrip.cta.href} className="cta-arrow-btn group h-auto max-w-full rounded-full bg-brand-red py-4 pl-7 pr-3 text-base font-bold text-white hover:bg-brand-red-dark lg:shrink-0">
              {forOrgWorkshopStrip.cta.label}
              <span className="cta-arrow-chip ml-3 grid size-9 place-items-center rounded-full bg-white/20 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5">
                <ArrowUpRight className="size-4" />
              </span>
            </ButtonLink>
          </div>
        </div>
      </div>
    </section>

    {/* ===================== WHO WE SERVE ===================== */}
    <section id="tracks" className="scroll-mt-32 py-14 md:py-20">
      <div className="wrap">
        <SectionHead eyebrow={forOrgSectionHeads.tracks.eyebrow} title={accent(forOrgSectionHeads.tracks.heading, "tracks")} sub={forOrgSectionHeads.tracks.sub} />
        <div className="for-org-tracks mt-10 grid gap-8 md:grid-cols-2">
          {forOrgTracks.map((t, i) => {
            const a = brandAccents[i % brandAccents.length];
            return (<article key={t.title} className="for-org-track overflow-hidden rounded-[2rem] border border-brand-navy/10 bg-card shadow-sm">
              <span aria-hidden className={`h-1 ${a.bar}`} />
              <div className="for-org-track__body px-9 py-9">
                <span className={`text-xs font-bold uppercase tracking-[0.18em] ${a.text}`}>
                  Track {i + 1}
                </span>
                <h3 className="for-org-track__title mt-3 font-display text-3xl font-semibold leading-tight text-heading">
                  {t.title}
                </h3>
                <p className="for-org-track__sub mt-4 text-sm leading-relaxed text-muted-foreground">
                  {t.sub}
                </p>
              </div>
              <div className="for-org-track__programmes px-9 py-7">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-brand-navy/50">
                  {t.label}
                </p>
                <div className="mt-4 flex flex-wrap gap-2.5">
                  {t.chips.map((c) => (<span key={c} className="rounded-full bg-cream-deep px-4 py-2 text-sm font-bold text-brand-navy">
                    {c}
                  </span>))}
                </div>
              </div>
            </article>);
          })}
        </div>
      </div>
    </section>

    {/* ===================== PAST PROGRAMMES ===================== */}
    <section id="programmes" className="scroll-mt-32 bg-cream-deep py-14 md:py-20">
      <div className="wrap">
        <SectionHead eyebrow={forOrgSectionHeads.programmes.eyebrow} title={accent(forOrgSectionHeads.programmes.heading, "action")} />
        {/* QA (31 Aug): smaller rectangular cards so this section reads
              differently from the other card grids; red category tags removed.
              Logos sit at ~40px. Diksha/IGNITE artwork is cropped tight so
              they read at the same visual size as the other marks. */}
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {forOrgProgrammes.map((p) => {
            const href = p.href || forOrgProgrammeLinks[p.name] || "/resources";
            const logos = p.logos || (p.logo ? [{ src: p.logo, alt: p.org || p.name }] : []);
            const dual = logos.length > 1;
            const split = dual && p.splitLogos;
            return (<Link key={p.name} href={href} aria-label={`${p.name} — read the programme story`} className="group flex flex-col rounded-2xl bg-card p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
              <div className={split
                ? "mb-3 grid h-14 w-full grid-cols-2 items-center gap-2"
                : "mb-3 flex h-12 w-full items-center justify-center gap-2"}>
                {logos.map((l) => (
                  <span key={l.src} className={split ? "flex h-full min-w-0 items-center justify-center" : "contents"}>
                    <img
                      src={l.src}
                      alt={l.alt}
                      className={split
                        ? "max-h-11 w-auto max-w-full object-contain"
                        : dual
                          ? "h-10 max-h-10 w-auto max-w-[42%] min-w-0 object-contain"
                          : "h-10 max-h-10 w-auto max-w-[12rem] object-contain"}
                    />
                  </span>
                ))}
              </div>
              <div className="relative min-w-0">
                <h3 className="px-9 text-center font-display text-base font-semibold leading-snug text-heading">
                  {p.name}
                </h3>
                <span className="absolute right-0 top-1/2 grid size-8 -translate-y-1/2 place-items-center rounded-full border border-brand-navy/15 text-brand-navy transition-all group-hover:border-brand-red group-hover:bg-brand-red group-hover:text-white">
                  <ArrowUpRight className="size-3.5" />
                </span>
              </div>
            </Link>);
          })}
        </div>
      </div>
    </section>

    {/* ===================== WHY PARTNER ===================== */}
    <section id="why" className="scroll-mt-32 py-14 md:py-20">
      <div className="wrap">
        <SectionHead eyebrow={forOrgSectionHeads.why.eyebrow} title={accent(forOrgSectionHeads.why.heading, "choose us")} />
        <div className="mt-10 grid gap-x-12 gap-y-10 md:grid-cols-2 lg:grid-cols-3">
          {forOrgWhy.map((w) => (<div key={w.n} className="border-t-2 border-brand-navy/10 pt-6">
            <h3 className="font-display text-2xl font-semibold text-heading">
              {w.title}
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              {w.desc}
            </p>
          </div>))}
        </div>
      </div>
    </section>

    {/* ===================== GALLERY ===================== */}
    <section className="bg-cream-deep py-14 md:py-20">
      <div className="wrap">
        <SectionHead eyebrow={forOrgSectionHeads.gallery.eyebrow} title={accent(forOrgSectionHeads.gallery.heading, "programmes")} />
        <div className="mt-10">
          <Suspense fallback={null}>
            <OrgGalleryCarousel />
          </Suspense>
        </div>
      </div>
    </section>

    {/* ===================== MENTORLOOKUP TEASER ===================== */}
    <section className="py-8 md:py-12">
      <div className="wrap">
        <div className="relative overflow-hidden rounded-[2.5rem] border border-brand-navy/10 bg-cream-deep px-8 py-12 md:px-16 md:py-14">
          <div aria-hidden className="pointer-events-none absolute -right-24 -top-24 size-[360px] rounded-full bg-brand-blue/15 blur-3xl" />
          {/* QA (31 Aug): button stacks below the copy until lg so it never
                crowds or clips the panel edge at tablet/mobile widths. */}
          <div className="relative flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
            <div className="max-w-2xl">
              <span className="flex items-center gap-2.5 text-xs font-bold uppercase tracking-[0.2em] text-brand-red">
                <span className="h-px w-8 bg-current opacity-50" />
                {forOrgCrossSell.eyebrow}
              </span>
              <h2 className="mt-3 font-display text-3xl font-semibold text-heading md:text-4xl">
                {forOrgCrossSell.heading}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                {forOrgCrossSell.sub}
              </p>
            </div>
            <ButtonLink href={forOrgCrossSell.cta.href} className="cta-arrow-btn group h-auto max-w-full rounded-full bg-brand-red py-4 pl-7 pr-3 text-base font-bold text-white hover:bg-brand-red-dark lg:shrink-0">
              {forOrgCrossSell.cta.label}
              <span className="cta-arrow-chip ml-3 grid size-9 place-items-center rounded-full bg-white/20 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5">
                <ArrowUpRight className="size-4" />
              </span>
            </ButtonLink>
          </div>
        </div>
      </div>
    </section>

    {/* ===================== CONTACT CTA ===================== */}
    <section id="contact" className="scroll-mt-32 py-8 md:py-12">
      <div className="wrap">
        {/* Light panel, in line with the rest of the site: the CTA reads on a
            white/#f8f8f8 canvas and the brand red stays on the buttons. */}
        {/* QA (31 Aug): left aligned, in line with every other section on the site. */}
        <div className="relative overflow-hidden rounded-[2.5rem] bg-cream-deep px-8 py-14 ring-1 ring-brand-navy/5 md:px-16 md:py-16">
          <div aria-hidden className="pointer-events-none absolute -left-20 bottom-0 size-[320px] rounded-full bg-brand-blue/15 blur-3xl" />
          <div className="relative">
            <h2 className="max-w-2xl font-display text-4xl font-semibold leading-tight text-heading md:text-6xl">
              {accent(forOrgFinalCta.heading, "people?", "font-normal italic text-brand-red")}
            </h2>
            <p className="mt-5 max-w-xl text-lg text-muted-foreground">
              {forOrgFinalCta.sub}
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <ButtonLink href={forOrgFinalCta.primary.href} className="group h-auto rounded-full bg-brand-red py-4 pl-7 pr-3 text-base font-bold text-white hover:bg-brand-red-dark">
                {forOrgFinalCta.primary.label}
                <span className="cta-arrow-chip ml-3 grid size-9 place-items-center rounded-full bg-white/20 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5">
                  <ArrowUpRight className="size-4" />
                </span>
              </ButtonLink>
              <ButtonLink href={forOrgFinalCta.secondary.href} download target="_blank" rel="noopener noreferrer" variant="ghost" className="h-auto rounded-full border border-brand-navy/25 bg-card px-7 py-4 text-base font-bold text-brand-navy hover:bg-brand-navy hover:text-white">
                {forOrgFinalCta.secondary.label}
              </ButtonLink>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>);
}
/* ---------- local helpers ---------- */
/** Wrap one phrase of a content string in the editorial italic accent. */
function accent(text, phrase, emClass = "font-normal italic text-brand-red") {
  const i = text.indexOf(phrase);
  if (i === -1)
    return text;
  return (<>
    {text.slice(0, i)}
    <em className={emClass}>{text.slice(i, i + phrase.length)}</em>
    {text.slice(i + phrase.length)}
  </>);
}
// QA (31 Aug): standard sitewide section head — left aligned, eyebrow with the
// rule line (the centered variant this page used was off-pattern).
function SectionHead({ eyebrow, title, sub, }) {
  return (<div className="max-w-3xl">
    <span className="mb-4 flex items-center gap-2.5 text-xs font-bold uppercase tracking-[0.2em] text-brand-red">
      <span className="h-px w-8 bg-current opacity-50" />
      {eyebrow}
    </span>
    <h2 className="font-display text-4xl font-semibold leading-[1.02] text-heading md:text-5xl">
      {title}
    </h2>
    {sub && (<p className="mt-5 text-lg leading-relaxed text-muted-foreground">
      {sub}
    </p>)}
  </div>);
}
